import { useCallback, useEffect, useState } from 'react'

const ADVISER_PANEL_SELECTOR = '.adviser-panel'

/**
 * The width an open Haven panel takes out of the page, so a viewport-fixed footer can stop
 * at its edge instead of running underneath it.
 *
 * On desktop the panel is an in-flow sibling: it shrinks the content column, but a fixed
 * element still spans the whole viewport. On mobile it goes full-screen and covers the
 * footer outright, so there is nothing to inset from.
 */
export const useAdviserPanelInset = (): number => {
  const [inset, setInset] = useState(0)

  const measure = useCallback(() => {
    const panel = document.querySelector(ADVISER_PANEL_SELECTOR)
    if (!panel) {
      setInset(0)
      return
    }
    if (window.getComputedStyle(panel).position === 'fixed') {
      setInset(0)
      return
    }
    const width = Math.round(panel.getBoundingClientRect().width)
    setInset((current) => (current === width ? current : width))
  }, [])

  useEffect(() => {
    measure()

    // The panel mounts and unmounts as the user opens and closes it, and it is resizable by
    // its own drag handle, so both the tree and the box have to be watched.
    const observer = new MutationObserver(() => measure())
    observer.observe(document.body, { childList: true, subtree: true })

    const resizeObserver = new ResizeObserver(() => measure())
    const panel = document.querySelector(ADVISER_PANEL_SELECTOR)
    if (panel) resizeObserver.observe(panel)

    window.addEventListener('resize', measure)

    return () => {
      observer.disconnect()
      resizeObserver.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure, inset])

  return inset
}
